import clsx from "clsx";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-material-ui";
import { Link } from "gatsby";
import { navigate } from "gatsby-link";
import { Button } from "gatsby-theme-material-ui";
import PropTypes from "prop-types";
import React, { ReactElement, useRef, useState } from "react";
import queryString from "query-string";
import { graphql } from "gatsby";
import DateFnsUtils from "@date-io/dayjs";
import {
  Container,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Theme,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import Content, { HTMLContent } from "../components/Content";
import Layout from "../components/Layout";
import Helmet from "react-helmet";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gridArea: "content",
      width: "100%",
      maxWidth: "800px",
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
      [theme.breakpoints.up("sm")]: {
        alignContent: "center",
        alignItems: "stretch",
        display: "grid",
        gap: `${theme.spacing(8)}px`,
        gridAutoFlow: "column",
        gridTemplateColumns: "2fr 2fr",
        paddingBottom: theme.spacing(4),
        paddingTop: theme.spacing(4),
      },
    },
    label: {
      '& a ' : {
        textDecoration: "underline",
        fontWeight: 500,
      }
    },
    form: {
      display: "grid",
      gap: `${theme.spacing(2)}px`,
      paddingBottom: theme.spacing(4),
      paddingTop: theme.spacing(4),
      '& .MuiFormControlLabel-labelPlacementStart' : {
        flexDirection: "row",
        marginLeft: 0,
      }
    },
    overview: {
      alignContent: "center",
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.secondary.contrastText,
      display: "grid",
      padding: theme.spacing(4),
    },
    radioGroup: {
      display: "flex",
      flexDirection: "column",
      '& .MuiFormControlLabel-labelPlacementStart' : {
        flexDirection: "row",
        marginLeft: 0,
      }
    },
    title: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
    previewImage: {
      width:"100%",
    },
    hiddenInput: {
      visibility:"hidden",
      height: "0",
      padding: "0",
    },
    infoWrapper: {
      display:"flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "12px",
      background: "white",
    },
    textInfo: {
      fontSize: "14px",
      display: "block",
      color: "black",
      width: "100%",
      marginBottom: "8px",
    },
    description: {},
  })
);

function encode(data): string {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

export const RegisterInterestTemplate = ({
  content,
  contentComponent,
  location
}): ReactElement => {
  const classes = useStyles();
  const PageContent = contentComponent || Content;
  const parsed = queryString.parse(location.search);
  const [checked, setChecked] = useState(false);
  const handleCheckChange = (event) => {
    setChecked(!checked)
  };
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ImageSrc = `/img/properties/${parsed.id}-1.jpg`
  const label = (
    <p className={classes.label}>
    I have read and agree with Williams Lynch <Link to="/privacy-policy">GPDR Policy*</Link>
    </p>
  )
  return (
    <Container className={clsx(classes.root)} component="main" fluid="fluid">
     
      <section className={clsx(classes.segment, classes.overview)}>
        <PageContent className="content" content={content} />
        <p>Property Reference: {parsed?.id}</p>
        <img src={ImageSrc} className={classes.previewImage} />
        <div className={classes.infoWrapper}>
          <span className={classes.textInfo}> {parsed?.name} </span>
          <span className={classes.textInfo}> {parsed?.price} </span>
        </div>
      </section>
      <section className={clsx(classes.segment)}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Formik
            initialValues={{
              email: "",
              first_name:"",
              last_name:"",
              registrant: "BUYER",
              id: parsed?.id || "",
            }}
            validate={(values): Partial => {
              const errors: Partial<Values> = {};

              if (!values.email) {
                errors.email = "Required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
              ) {
                errors.email = "Invalid email address";
              }
              if(!values.first_name) {
                errors.first_name = "Required"
              }
              if(!values.last_name) {
                errors.last_name = "Required"
              }
              return errors;
            }}
            onSubmit={(values): void => {
              const form = formRef.current;
              console.log(values)
              setIsSubmitting(true);

              fetch(form.getAttribute("action"), {
                method: "POST",
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
                body: encode({
                  "form-name": form.getAttribute("name"),
                  'subject': "ONLINE ENQUIRY – Register Form",
                  ...values,
                }),
              })
                .then(() => setIsSubmitting(false))
                .then(() => navigate(form.getAttribute("action")))
                .catch((error) => alert(error));
            }}
          >
            {({ submitForm, isSubmitting }): ReactElement => (
              <Form
                action="/register/thanks/"
                className={clsx(classes.form)}
                data-netlify-honeypot="bot-field"
                data-netlify="true"
                method="post"
                name="register-interest"
                ref={formRef}
              >
                {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
                <input type="hidden" name="form-name" value="contact" />
                <input type="hidden" id="subject" name="subject" value={`ONLINE ENQUIRY – Register Interest Form ${parsed?.id}` } />
                <Field
                  component={TextField}
                  label="First Name"
                  name="first_name"
                  type="name"
                  required
                />

                <Field
                  component={TextField}
                  label="Last Name"
                  name="last_name"
                  type="name"
                  required
                />

                <Field
                  component={TextField}
                  label="Telephone"
                  name="telephone"
                  type="tel"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                />

                <Field
                  component={TextField}
                  label="Email"
                  name="email"
                  type="email"
                  required
                />

                <Field
                  component={TextField}
                  label="More Details of Your Requirements"
                  name="requirements"
                />

                <Field
                  component={TextField}
                  label="id"
                  name="id"
                  className={clsx(classes.hiddenInput)}
                />

                <FormControlLabel
                  value="GPDR"
                  control={
                    <Checkbox
                      name="GPDR"
                      checked={checked}
                      onChange={handleCheckChange}
                      color="secondary"
                    />
                  }
                  labelPlacement="end"
                  label={label}
                />
                {isSubmitting && <LinearProgress />}

                <Button
                  variant="contained"
                  disabled={isSubmitting || checked !== true}
                  onClick={submitForm}
                >
                  Register Now
                </Button>
              </Form>
            )}
          </Formik>
        </MuiPickersUtilsProvider>
      </section>
    </Container>
  );
};

RegisterInterestTemplate.propTypes = {
  title: PropTypes.string,
  heading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string,
  subheading: PropTypes.string,
  description: PropTypes.string,
  formTitle: PropTypes.string.isRequired,
  content: PropTypes.string,
  contentComponent: PropTypes.func,
  helmet: PropTypes.object
};

const RegisterPage = ({ data, location }): ReactElement => {
  const { markdownRemark: post } = data;
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <RegisterInterestTemplate
        title={post.frontmatter.title}
        heading={post.frontmatter.heading}
        subheading={post.frontmatter.subheading}
        description={post.frontmatter.description}
        featuredimage={post.frontmatter.featuredimage}
        imagewidth={post.frontmatter.imagewidth}
        contentComponent={HTMLContent}
        formTitle={post.frontmatter.formTitle}
        content={post.html}
        location={location}
        helmet={
          <Helmet titleTemplate="%s">
            <title>{`${post.frontmatter.title}`}</title>
            <meta name="description" content={`${post.frontmatter.description}`} />
            <meta name="image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="og:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="twitter:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
          </Helmet>
        }
      />
    </Layout>
  );
};

RegisterPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object,
};

export default RegisterPage;

export const pageQuery = graphql`
  query RegisterInterest($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        heading
        subheading
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        description
        formTitle
      }
    }
  }
`;
